import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from 'utils';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { convertToHTML } from 'draft-convert';
import { MasterSheetContainer } from '../containers/useMasterSheetContainer';
import { PermissionsProvider } from 'providers/permissionsProvider';

interface JobOption {
  id: string;
  name: string;
  description?: string;
  isInternal?: boolean;
}
import { CAN_EDIT_NOTES, CAN_EDIT_OPTIONS } from 'providers/permissions';
import { useStyles } from '../views/master-sheet-styles';
import { useDataProvider, useNotify } from 'react-admin';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

export const Notes: React.FC = () => {
  const { selectedJob } = MasterSheetContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [notes, setNotes] = useState<EditorState>(EditorState.createEmpty());
  const [options, setOptions] = useState<JobOption[]>([]);
  const [optionsSelected, setOptionsSelected] = useState<string[]>([]);
  const [jobNotes, setJobNotes] = useState<any[]>([]);

  // Initialize notes and options from API
  useEffect(() => {
    const loadNotesData = async () => {
      if (!selectedJob?.id) {
        setLoadingNotes(false);
        return;
      }

      try {
        setLoadingNotes(true);
        const jobId = selectedJob.id;

        // Load job notes
        const jobNotesResponse = await dataProvider.getList('job-note', {
          filter: { jobId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'DESC' },
        });

        if (jobNotesResponse.data) {
          setJobNotes(jobNotesResponse.data);
          // Keep editor empty - don't load the last note into the editor
        }

        // Load job options (selected options)
        const jobOptionsResponse = await dataProvider.getList('job-option', {
          filter: { jobId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'ASC' },
        });

        // Load all available options with isInternal filter (like original)
        const allOptionsResponse = await dataProvider.getList('option', {
          filter: { isInternal: true },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'ASC' },
        });

        if (allOptionsResponse.data && jobOptionsResponse.data) {
          // Map options with selected state
          const mappedOptions: JobOption[] = allOptionsResponse.data.map(
            (opt: any) => ({
              id: opt.id,
              name: opt.name,
              selected: jobOptionsResponse.data.some(
                (jobOpt: any) => jobOpt.optionId === opt.id,
              ),
            }),
          );

          setOptions(mappedOptions);
          setOptionsSelected(
            jobOptionsResponse.data.map((jobOpt: any) => jobOpt.optionId),
          );
        }
      } catch (error) {
        console.error('Error loading notes data:', error);
        notify(`Error loading notes: ${error.message}`, 'error');
      } finally {
        setLoadingNotes(false);
      }
    };

    loadNotesData();
  }, [selectedJob?.id, dataProvider, notify]);

  const getCheckedOption = (option: JobOption) => {
    return optionsSelected.includes(option.id);
  };

  const onChangeOption = (option: JobOption) => {
    if (!hasPermission(CAN_EDIT_OPTIONS) || !selectedJob?.id) return;

    setOptionsSelected(prev => {
      if (prev.includes(option.id)) {
        return prev.filter(id => id !== option.id);
      } else {
        return [...prev, option.id];
      }
    });
  };

  const changeNotes = async () => {
    if (!selectedJob?.id || !hasPermission(CAN_EDIT_NOTES)) return;

    setLoading(true);
    const plainText =
      convertToHTML(notes.getCurrentContent()).trim() !== '<p></p>'
        ? convertToHTML(notes.getCurrentContent()).trim()
        : '';

    try {
      const jobId = selectedJob.id;

      // Format options like V1
      const formattedOptions = optionsSelected.map(optionId => ({
        optionId,
      }));

      // Create job note with options (same as V1)
      await dataProvider.create('job-note', {
        data: {
          jobId,
          ...(plainText && { note: plainText }),
          ...(formattedOptions.length && {
            jobNoteOptions: formattedOptions,
          }),
        },
      });

      // Reload notes to show the new entry
      const jobNotesResponse = await dataProvider.getList('job-note', {
        filter: { jobId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
      });

      if (jobNotesResponse.data) {
        setJobNotes(jobNotesResponse.data);
      }

      // Clear the editor after saving
      setNotes(EditorState.createEmpty());
    } catch (error) {
      console.error('Error saving notes:', error);
      notify(`Error saving notes: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const onEditorStateChange = (editorState: EditorState) => {
    setNotes(editorState);
  };

  return (
    <Box width="100%" style={{ overflow: 'hidden' }}>
      <Box className={classes.titleTabNoBorder} px={2}>
        <Typography className={classes.titleFont}>Notes</Typography>
      </Box>

      {loadingNotes ? (
        <Box
          display="flex"
          width="100%"
          justifyContent="center"
          flexDirection="row"
          my={2}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Options Section */}
          {options.length > 0 && (
            <Box
              className={classes.optionsSection}
              px={2}
              style={{ overflow: 'hidden' }}
            >
              {options.map((option, index) => (
                <Box key={option.id} mr={index === options.length - 1 ? 0 : 2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={getCheckedOption(option)}
                        onChange={() => onChangeOption(option)}
                        disabled={!hasPermission(CAN_EDIT_OPTIONS)}
                        name={option.name}
                        color="primary"
                      />
                    }
                    label={option.name}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Editor Section */}
          <Box mt={2}>
            {hasPermission(CAN_EDIT_NOTES) ? (
              <Editor
                editorState={notes}
                editorClassName={classes.editor}
                toolbar={{
                  options: ['inline', 'blockType', 'list'],
                  fontSize: {
                    options: [12, 14, 16, 18, 24, 30],
                  },
                  inline: {
                    options: ['bold', 'italic', 'underline'],
                  },
                  blockType: {
                    inDropdown: true,
                    options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  list: {
                    inDropdown: false,
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                    options: ['unordered', 'ordered'],
                  },
                }}
                onEditorStateChange={onEditorStateChange}
                wrapperClassName="demo-wrapper"
                toolbarClassName="demo-toolbar"
                editorStyle={{
                  border: '1px solid #ccc',
                  padding: '5px',
                  borderRadius: '2px',
                  minHeight: '80px',
                }}
              />
            ) : (
              <Box px={2}>
                <Typography variant="caption" color="textSecondary">
                  Read Only
                </Typography>
                <Box className={classes.readOnlyNotesBox}>
                  {jobNotes.length > 0 && jobNotes[0].note ? (
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(jobNotes[0].note),
                      }}
                    />
                  ) : (
                    <Typography color="textSecondary">
                      No notes available.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Save Button */}
          {hasPermission(CAN_EDIT_NOTES) && (
            <Box mt={2} px={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={changeNotes}
                disabled={loading}
                size="large"
                className={classes.button}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    Saving...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          )}

          {/* Notes History */}
          {jobNotes.length > 0 && (
            <Box px={2} pt={2}>
              <Typography style={{ fontWeight: 'bold' }}>
                Notes History
              </Typography>
              {jobNotes.map((item: any, index: number) => (
                <Box
                  key={item.id}
                  mb={2}
                  borderTop={index === 0 ? '' : 1}
                  borderColor="lightgray"
                  pt={2}
                >
                  <Box>
                    <Box>
                      <Typography>
                        {item.user?.firstName && item.user?.lastName
                          ? `${item.user.firstName} ${item.user.lastName} - `
                          : ''}
                        {item.createdAt &&
                          `${new Date(item.createdAt).toLocaleTimeString(
                            'en-US',
                          )} ${new Date(item.createdAt).toLocaleDateString(
                            'en-US',
                          )}`}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      {item.note && (
                        <Box
                          style={{
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(item.note),
                          }}
                        />
                      )}
                      {item.jobNoteOptions &&
                        item.jobNoteOptions.length > 0 && (
                          <Typography
                            style={{ marginTop: 8, fontStyle: 'italic' }}
                          >
                            Job Options:{' '}
                            {item.jobNoteOptions
                              .map(
                                (opt: any) => opt.option?.name || opt.optionId,
                              )
                              .join(', ')}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Notes;

